import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { supabase } from '../lib/supabase';
import { useNotifications } from './useNotifications';
import { isJobFinished, isJobProcessing, jobUpdateEffects, normalizeJobRecord } from '../utils/jobStatus'

const AppContext = createContext()

export function useApp() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within an AppProvider')
  }
  return context
}

export function AppProvider({ children }) {
  const [jobs, setJobs] = useState(new Map())
  const [activeProcessing, setActiveProcessing] = useState(false)
  const [statusCheckInterval, setStatusCheckInterval] = useState(null)
  const [llmProviderName, setLlmProviderName] = useState('AI')
  const [appVersion, setAppVersion] = useState('Loading...')
  const [user, setUser] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [jobsRevision, setJobsRevision] = useState(0);
  const notifications = useNotifications();
  const notificationsRef = useRef(notifications)
  const loadJobsHistoryRef = useRef(null)
  const checkJobStatusRef = useRef(null)
  const addJobRef = useRef(null)
  const updateJobRef = useRef(null)
  const removeJobRef = useRef(null)
  const processingJobsRef = useRef([])
  const statusCheckIntervalRef = useRef(null)
  notificationsRef.current = notifications
  
  // Memoize expensive operations
  const sortedJobs = useMemo(() => 
    Array.from(jobs.values()).sort((a, b) => 
      new Date(b.created_at) - new Date(a.created_at)
    ), [jobs]
  )

  const processingJobs = useMemo(() =>
    Array.from(jobs.values()).filter(job =>
      isJobProcessing(job.status) && job.action_generation_status !== 'failed'
    ), [jobs]
  )
  processingJobsRef.current = processingJobs

  // Load app version on mount
  useEffect(() => {
    loadAppVersion()
    loadLLMProviderName()
    
    // Initialize auth state
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setUser(session?.user || null);
      } catch (error) {
        console.warn('Failed to get initial session:', error);
        setUser(null);
      }
    };
    
    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user || null);
      }
    );
    
    return () => {
      subscription.unsubscribe();
    };
  }, [])

  // Backup polling for processing jobs
  const hasProcessingJobs = processingJobs.length > 0
  useEffect(() => {
    if (!hasProcessingJobs) {
      return
    }

    const interval = setInterval(async () => {
      for (const job of processingJobsRef.current) {
        if (checkJobStatusRef.current) {
          await checkJobStatusRef.current(job.id)
        }
      }
    }, 5000)

    setPollingInterval(interval)

    return () => {
      clearInterval(interval)
      setPollingInterval(null)
    }
  }, [hasProcessingJobs])

  useEffect(() => {
    if (user) {
      const channel = supabase.channel(`jobs:${user.id}`);
  
      channel
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'jobs', 
          filter: `user_id=eq.${user.id}` 
        }, payload => {
          console.log('Real-time update received:', payload);
          const incoming = normalizeJobRecord(payload.new || payload.old)
          
          if (payload.eventType === 'INSERT' && incoming) {
            console.log('Adding new job:', incoming);
            addJobRef.current?.(incoming);
          } else if (payload.eventType === 'UPDATE' && incoming) {
            console.log('Updating job:', incoming);
            updateJobRef.current?.(incoming.id, incoming);
          } else if (payload.eventType === 'DELETE' && incoming) {
            console.log('Removing job:', incoming);
            removeJobRef.current?.(incoming.id);
          }
        })
        .subscribe((status) => {
          console.log('Supabase subscription status:', status);
        });
  
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);

  const loadAppVersion = async () => {
    try {
      const response = await fetch('/api/version')
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      setAppVersion(data.version || '1.0.0')
    } catch (error) {
      console.error('Failed to load app version:', error)
      setAppVersion('1.0.0')
    }
  }

  const loadLLMProviderName = async () => {
    try {
      // For now, set a default since the endpoint doesn't exist yet
      setLlmProviderName('Grok AI')
    } catch (error) {
      console.error('Failed to load LLM provider:', error)
      setLlmProviderName('AI')
    }
  }

  // Job management
  const addJob = useCallback((job) => {
    const normalized = normalizeJobRecord(job) || job
    if (!normalized?.id) return
    setJobs(prev => new Map(prev.set(normalized.id, { ...prev.get(normalized.id), ...normalized })))
  }, [])

  const updateJob = useCallback((jobId, updates) => {
    const normalizedUpdates = normalizeJobRecord({ ...updates, id: updates?.id || jobId }) || { ...updates, id: jobId }
    const resolvedId = normalizedUpdates.id || jobId
    let pendingEffects = { showActionsReady: false, reloadJobs: false }
    let notifiedJob = null

    setJobs(prev => {
      const newJobs = new Map(prev)
      const existingJob = newJobs.get(resolvedId) || newJobs.get(jobId)
      const updatedJob = existingJob
        ? { ...existingJob, ...normalizedUpdates, id: existingJob.id }
        : { ...normalizedUpdates, id: resolvedId }

      newJobs.set(updatedJob.id, updatedJob)
      pendingEffects = jobUpdateEffects(existingJob, updatedJob)
      notifiedJob = updatedJob
      return newJobs
    })

    if (pendingEffects.showActionsReady && notificationsRef.current?.permission === 'granted') {
      notificationsRef.current.showActionsReadyNotification(notifiedJob)
    }

    if (pendingEffects.reloadJobs) {
      queueMicrotask(() => {
        loadJobsHistoryRef.current?.({ bumpRevision: true })
      })
    }
  }, [])

  const removeJob = useCallback((jobId) => {
    setJobs(prev => {
      const newJobs = new Map(prev)
      newJobs.delete(jobId)
      return newJobs
    })
  }, [])

  const getJob = useCallback((jobId) => {
    return jobs.get(jobId)
  }, [jobs])

  const getAllJobs = useCallback(() => {
    return sortedJobs
  }, [sortedJobs])

  // Processing status management
  const stopProcessing = useCallback(() => {
    setActiveProcessing(false)

    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current)
      statusCheckIntervalRef.current = null
    }

    setStatusCheckInterval(null)
  }, [])

  const checkJobStatus = useCallback(async (jobId) => {
    try {
      const authToken = localStorage.getItem('auth_token')
      const url = `/api/v1/jobs/${jobId}`
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const response_data = await response.json()
        
        if (response_data.success && response_data.data) {
          const jobData = normalizeJobRecord(response_data.data) || response_data.data
          updateJob(jobData.id || jobId, jobData)
          
          const isFinalState = isJobFinished(jobData.status) ||
            jobData.action_generation_status === 'failed'

          if (isFinalState) {
            console.log('[useApp] Job finished, stopping polling:', {
              jobId,
              status: jobData.status,
              action_generation_status: jobData.action_generation_status
            })
            stopProcessing()
          }
          
          return jobData
        }
      } else {
        // If we get a 404, the job might not exist anymore or there's an endpoint issue
        if (response.status === 404) {
          stopProcessing()
          // Remove the job from the UI since it doesn't exist on the server
          removeJob(jobId)
        }
      }
    } catch (error) {
      console.error('[useApp] Job status check error:', error)
    }
  }, [updateJob, stopProcessing, removeJob])

  const startProcessing = useCallback((jobId) => {
    setActiveProcessing(true)

    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current)
    }

    const interval = setInterval(async () => {
      if (checkJobStatusRef.current) {
        await checkJobStatusRef.current(jobId)
      }
    }, 2000)

    statusCheckIntervalRef.current = interval
    setStatusCheckInterval(interval)
  }, [])

  // Load jobs history
  const loadJobsHistory = useCallback(async (options = {}) => {
    try {
      const authToken = localStorage.getItem('auth_token')
      if (!authToken) return

      const response = await fetch('/api/v1/jobs', {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        
        // Backend returns { success: true, data: { active: [], completed: [], total: 0 } }
        const jobsData = result.success ? result.data : result
        const jobsMap = new Map()
        
        // Combine active and completed jobs
        const allJobs = [...(jobsData.active || []), ...(jobsData.completed || [])]
        
        allJobs.forEach(job => {
          const normalized = normalizeJobRecord(job) || job
          if (normalized?.id) {
            jobsMap.set(normalized.id, normalized)
          }
        })
        setJobs(jobsMap)
        if (options.bumpRevision) {
          setJobsRevision(n => n + 1)
        }
      }
    } catch (error) {
      console.error('Failed to load jobs history:', error)
    }
  }, [])

  addJobRef.current = addJob
  updateJobRef.current = updateJob
  removeJobRef.current = removeJob
  loadJobsHistoryRef.current = loadJobsHistory
  checkJobStatusRef.current = checkJobStatus

  // Submit URL for processing
  const processURL = useCallback(async (url, options = {}) => {
    try {
      const authToken = localStorage.getItem('auth_token')
      
      if (!authToken) {
        throw new Error('Authentication required')
      }

      const response = await fetch('/api/v1/process', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url, ...options })
      })

      const data = await response.json()

      if (response.ok) {
        // Backend returns { success: true, data: jobObject }
        const newJob = data.data || data.job || data
        addJob(newJob)
        startProcessing(newJob.id)
        return { success: true, job: newJob }
      } else {
        // Return the full error response for better error handling
        return { 
          success: false, 
          error: data.error || 'Processing failed',
          code: data.code,
          reason: data.reason,
          credits_required: data.credits_required,
          credits_available: data.credits_available,
          ...data
        }
      }
    } catch (error) {
      return { success: false, error: error.message || 'Network error' }
    }
  }, [addJob, startProcessing])

  // Process batch URLs
  const processBatchURLs = useCallback(async (urls, options = {}) => {
    try {
      const authToken = localStorage.getItem('auth_token')
      if (!authToken) {
        throw new Error('Authentication required')
      }

      const response = await fetch('/api/v1/process/batch', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ urls, ...options })
      })

      const data = await response.json()

      if (response.ok) {
        // Add all jobs to state
        if (data.jobs) {
          data.jobs.forEach(job => addJob(job))
        }
        return { success: true, jobs: data.jobs }
      } else {
        // Return the full error response for better error handling
        return { 
          success: false, 
          error: data.error || 'Batch processing failed',
          code: data.code,
          reason: data.reason,
          credits_required: data.credits_required,
          credits_available: data.credits_available,
          ...data
        }
      }
    } catch (error) {
      console.error('Batch processing error:', error)
      return { success: false, error: error.message || 'Network error' }
    }
  }, [addJob])

  // Force clear failed job from polling (emergency cleanup)
  const clearFailedJob = useCallback((jobId) => {
    console.log('[useApp] Force clearing failed job from polling:', jobId)
    const job = jobs.get(jobId)
    if (job && (job.status === 'failed' || job.action_generation_status === 'failed')) {
      updateJob(jobId, { ...job, status: 'failed', action_generation_status: 'failed' })
      stopProcessing()
      if (pollingInterval) {
        clearInterval(pollingInterval)
        setPollingInterval(null)
      }
    }
  }, [jobs, updateJob, stopProcessing, pollingInterval])

  // Delete a specific job
  const deleteJob = useCallback(async (jobId) => {
    try {
      const authToken = localStorage.getItem('auth_token')
      if (!authToken) {
        throw new Error('Authentication required')
      }

      const response = await fetch(`/api/v1/jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok) {
        removeJob(jobId)
        return { success: true, message: data.message }
      } else {
        return { success: false, error: data.error || 'Delete failed' }
      }
    } catch (error) {
      console.error('Job deletion error:', error)
      return { success: false, error: error.message || 'Network error' }
    }
  }, [removeJob])

  // Clear all jobs for the current user
  const clearAllJobs = useCallback(async () => {
    try {
      const authToken = localStorage.getItem('auth_token')
      if (!authToken) {
        throw new Error('Authentication required')
      }

      const response = await fetch('/api/v1/jobs', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()

      if (response.ok) {
        setJobs(new Map()) // Clear local state
        return { success: true, message: data.message, deletedCount: data.data?.deleted_count }
      } else {
        return { success: false, error: data.error || 'Clear failed' }
      }
    } catch (error) {
      console.error('Clear jobs error:', error)
      return { success: false, error: error.message || 'Network error' }
    }
  }, [])

  const value = useMemo(() => ({
    // State
    jobs: sortedJobs,
    activeProcessing,
    llmProviderName,
    appVersion,
    jobsRevision,
    
    // Job management
    addJob,
    updateJob,
    removeJob,
    getJob,
    getAllJobs,
    loadJobsHistory,
    deleteJob,
    clearAllJobs,
    clearFailedJob,
    
    // Processing
    processURL,
    processBatchURLs,
    startProcessing,
    stopProcessing,
    checkJobStatus,
    
    // App data
    loadAppVersion,
    loadLLMProviderName,
    
    // Notifications
    notifications
  }), [
    sortedJobs,
    activeProcessing,
    llmProviderName,
    appVersion,
    jobsRevision,
    addJob,
    updateJob,
    removeJob,
    getJob,
    loadJobsHistory,
    deleteJob,
    clearAllJobs,
    processURL,
    processBatchURLs,
    startProcessing,
    stopProcessing,
    checkJobStatus,
    loadAppVersion,
    loadLLMProviderName,
    notifications
  ])

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}